/**
 * Static mapping of country -> bank name -> SWIFT/BIC code.
 * Covers the most common banks across key investor markets.
 * Bank names are stored lowercase for case-insensitive matching.
 */
const SWIFT_MAPPING: Record<string, Record<string, string>> = {
  GB: {
    hsbc: "HBUKGB4B",
    barclays: "BUKBGB22",
    lloyds: "LOYDGB2L",
    natwest: "NWBKGB2L",
    "royal bank of scotland": "RBOSGB2L",
    "standard chartered": "SCBLGB2L",
    nationwide: "NATWGB21",
    santander: "ABBYGB2L",
    halifax: "HLFXGB21",
    "bank of scotland": "BOFSGB21",
    monzo: "MONZGB2L",
    starling: "SRLGGB2L",
    revolut: "REVOGB21",
    "virgin money": "NAIAGB21",
    metro: "MYMBGB2L",
    tsb: "TSBSGB2A",
    coutts: "COUTGB22",
  },
  US: {
    "jpmorgan chase": "CHASUS33",
    "bank of america": "BOFAUS3N",
    "wells fargo": "WFBIUS6S",
    citibank: "CITIUS33",
    "goldman sachs": "GSCRUS33",
    "morgan stanley": "MSTCUS33",
    "us bank": "USBKUS44",
    pnc: "PNCCUS33",
    "capital one": "HIBKUS44",
    "td bank": "TDOMUS33",
  },
  AE: {
    "emirates nbd": "EBILAEAD",
    "abu dhabi commercial": "ADCBAEAA",
    "first abu dhabi": "NBADAEAA",
    "dubai islamic": "DUIBAEAD",
    mashreq: "BOMLAEAD",
    "commercial bank of dubai": "CBDUAEAD",
    "sharjah islamic": "NBSHAEAD",
    enbd: "EBILAEAD",
    adib: "ABDIAEAD",
    "rakbank": "NABORAEA",
  },
  SA: {
    "al rajhi": "RJHISARI",
    "saudi national": "NCBKSAJE",
    "saudi british": "SABBSARI",
    riyad: "RIBLSARI",
    "banque saudi fransi": "BSFRSARI",
    "arab national": "ARNBSARI",
    alinma: "INMASARI",
    "bank aljazira": "BJAZSAJE",
  },
  MY: {
    maybank: "MBBEMYKL",
    cimb: "CIBBMYKL",
    "public bank": "PBBEMYKL",
    "rhb bank": "RHBBMYKL",
    "hong leong": "HLBBMYKL",
    ambank: "AMMBMYKL",
    "bank islam": "BIMBMYKL",
    "bank muamalat": "BMMBMYKL",
  },
  PK: {
    "habib bank": "HABORPKA",
    "united bank": "UNILPKKA",
    "mcb bank": "MUCBPKKA",
    "allied bank": "ABPAPKKA",
    "bank alfalah": "ALFHPKKAXXX",
    "meezan bank": "MEZNPKKA",
    "standard chartered pk": "SCBLPKKX",
    "faysal bank": "FABORPKA",
  },
  DE: {
    "deutsche bank": "DEUTDEFF",
    commerzbank: "COBADEFF",
    "dresdner bank": "DRESDEFF",
    "unicredit bank": "HYVEDEMMXXX",
    "dz bank": "GENODEFF",
    ing: "INGDDEFF",
    "n26": "NTSBDEB1",
  },
  FR: {
    "bnp paribas": "BNPAFRPP",
    "societe generale": "SOGEFRPP",
    "credit agricole": "AGRIFRPP",
    "credit mutuel": "CMCIFRPP",
    lcl: "CRLYFRPP",
    "la banque postale": "PSSTFRPP",
  },
  NL: {
    "ing bank": "INGBNL2A",
    "abn amro": "ABNANL2A",
    rabobank: "RABONL2U",
    "de volksbank": "SNSBNL2A",
    bunq: "BUNQNL2A",
  },
  BE: {
    "kbc bank": "KREDBEBB",
    "bnp paribas fortis": "GEBABEBB",
    belfius: "GKCCBEBB",
    ing: "BBRUBEBB",
  },
  ES: {
    "banco santander": "BSCHESMM",
    bbva: "BBVAESMM",
    caixabank: "CAIXESBB",
    sabadell: "BSABESBB",
  },
  IT: {
    "intesa sanpaolo": "BCITITMM",
    "unicredit italy": "UNCRITMM",
    "banca mediolanum": "MEDBITMMXXX",
  },
  IE: {
    "bank of ireland": "BOFIIE2D",
    "allied irish": "AIBKIE2D",
    "permanent tsb": "IPBSIE2D",
  },
  AT: {
    "erste bank": "GIBAATWW",
    raiffeisen: "RLNWATWW",
    "bank austria": "BKAUATWW",
  },
  CH: {
    ubs: "UBSWCHZH",
    "credit suisse": "CRESCHZZ",
    "julius baer": "BAERCHZZ",
  },
  CA: {
    "royal bank of canada": "ROYCCAT2",
    td: "TDOMCATTTOR",
    "bank of montreal": "BOFMCAM2",
    scotiabank: "NOSCCATT",
    cibc: "CIBCCATT",
  },
  AU: {
    commonwealth: "CTBAAU2S",
    anz: "ANZBAU3M",
    westpac: "WPACAU2S",
    nab: "NATAAU33",
  },
  IN: {
    "state bank of india": "SBININBB",
    hdfc: "HDFCINBB",
    icici: "ICICINBB",
    "axis bank": "UTIBINBB",
    "kotak mahindra": "KKBKINBB",
  },
  QA: {
    "qatar national": "QNBAQAQA",
    "commercial bank of qatar": "CBQAQAQA",
    doha: "DOHBQAQA",
  },
  KW: {
    "national bank of kuwait": "NBOKKWKW",
    "kuwait finance house": "KFHOKWKW",
    boubyan: "BOUBKWKW",
  },
  BH: {
    "national bank of bahrain": "NBOBBHBM",
    "ahli united": "AUBBBHBM",
    "bank of bahrain": "BBKUBHBM",
  },
  OM: {
    "bank muscat": "BMUSOMRX",
    "national bank of oman": "NBOMOMRX",
    "bank dhofar": "BKDHOMRX",
  },
  TR: {
    garanti: "TGBATRIS",
    "is bankasi": "ISBKTRIS",
    "yapi kredi": "YAPITRIS",
    akbank: "AKBKTRIS",
    "kuveyt turk": "KTEFTRIS",
  },
};

const SUFFIXES_TO_STRIP = [
  "bank",
  "plc",
  "ltd",
  "limited",
  "group",
  "holdings",
  "inc",
  "corporation",
  "corp",
  "ag",
  "sa",
  "nv",
  "berhad",
  "bhd",
];
const MIN_FUZZY_MATCH_LENGTH = 3;

function normalize(name: string): string {
  let normalized = name.toLowerCase().trim();
  for (const suffix of SUFFIXES_TO_STRIP) {
    normalized = normalized.replace(new RegExp(`\\b${suffix}\\b`, "g"), "");
  }
  return normalized.replace(/\s+/g, " ").trim();
}

/**
 * Look up a SWIFT/BIC code by country and bank name.
 * Uses case-insensitive fuzzy matching (normalized includes check).
 * Returns null if no match found.
 */
export function lookupSwiftCode(
  countryCode: string,
  bankName: string
): string | null {
  const countryMap = SWIFT_MAPPING[countryCode.toUpperCase()];
  if (!countryMap) return null;

  const normalizedInput = normalize(bankName);
  if (!normalizedInput) return null;

  // Exact match first (against normalized keys)
  for (const [key, swift] of Object.entries(countryMap)) {
    if (normalize(key) === normalizedInput) {
      return swift;
    }
  }

  // Fuzzy match: normalized input contains normalized key or vice versa
  for (const [key, swift] of Object.entries(countryMap)) {
    const normalizedKey = normalize(key);
    if (
      normalizedInput.length >= MIN_FUZZY_MATCH_LENGTH &&
      normalizedKey.length >= MIN_FUZZY_MATCH_LENGTH &&
      (normalizedInput.includes(normalizedKey) ||
        normalizedKey.includes(normalizedInput))
    ) {
      return swift;
    }
  }

  return null;
}

/**
 * Get an example SWIFT/BIC code for a given country, useful as a placeholder hint.
 * Returns null if no examples exist for the country.
 */
export function getSwiftExampleForCountry(
  countryCode?: string | null
): string | null {
  if (!countryCode) return null;

  const countryMap = SWIFT_MAPPING[countryCode.toUpperCase()];
  if (!countryMap) return null;

  const firstKey = Object.keys(countryMap)[0];
  return firstKey ? countryMap[firstKey] : null;
}
